/**
 * Search templates (routes/searchTemplates.js, migration 228): save the
 * filters built on Search / Contact Search under a name, load them again
 * later, optionally share them with the team. Sits above the describe /
 * Build search box.
 *
 * Also the "you'll lose these filters" prompt: with filters applied that
 * aren't saved in a template, leaving through the sidebar asks first
 * (navGuard.ts), and closing / reloading the tab gets the browser's prompt.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { leaveGuardReleased, setLeaveGuard } from './navGuard';

type Template = { id: number; name: string; state: any; shared: boolean; mine: boolean; owner: string; updated_at: string };

export const SearchTemplatesBar: React.FC<{
  page: 'search' | 'contact-search';
  hasFilters: boolean;
  getState: () => any;
  applyState: (state: any) => void;
  tone?: 'teal' | 'violet';
  // Changes when the page loads filters that are saved elsewhere (a
  // project's own search): those count as saved, no prompt on leaving.
  baselineKey?: string;
}> = ({ page, hasFilters, getState, applyState, tone = 'teal', baselineKey }) => {
  const { token, user } = useAuth();
  const headers = useCallback((): Record<string, string> => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [listOpen, setListOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [loadedId, setLoadedId] = useState<number | null>(null);
  // JSON of the state last saved / loaded: anything else is unsaved work.
  const [savedJson, setSavedJson] = useState<string | null>(null);
  const [leaveAsk, setLeaveAsk] = useState<null | (() => void)>(null);
  const accent = tone === 'violet'
    ? { btn: 'text-violet-700 border-violet-200 hover:bg-violet-50', solid: 'bg-violet-600 hover:bg-violet-700', ring: 'focus:ring-violet-500' }
    : { btn: 'text-teal-700 border-teal-200 hover:bg-teal-50', solid: 'bg-teal-600 hover:bg-teal-700', ring: 'focus:ring-teal-500' };

  const load = useCallback(() => {
    fetch(`/api/search-templates?page=${page}`, { headers: headers() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setTemplates(d.templates || []); })
      .catch(() => {});
  }, [page, headers]);
  useEffect(() => { load(); }, [load]);

  const unsaved = hasFilters && JSON.stringify(getState()) !== savedJson;
  const unsavedRef = useRef(unsaved);
  unsavedRef.current = unsaved;

  // Sidebar navigation asks first; closing / reloading gets the browser prompt.
  useEffect(() => {
    setLeaveGuard((proceed) => {
      if (!unsavedRef.current) return false;
      setLeaveAsk(() => proceed);
      return true;
    });
    const onUnload = (e: BeforeUnloadEvent) => {
      if (!unsavedRef.current || leaveGuardReleased()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => { setLeaveGuard(null); window.removeEventListener('beforeunload', onUnload); };
  }, []);

  const save = async (): Promise<boolean> => {
    const n = name.trim();
    if (!n) { setNote('Give the template a name'); return false; }
    setBusy(true); setNote(null);
    try {
      const state = getState();
      const r = await fetch('/api/search-templates', { method: 'POST', headers: headers(), body: JSON.stringify({ page, name: n, state, shared }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setNote(d.error || 'Could not save the template'); return false; }
      setSavedJson(JSON.stringify(state));
      setLoadedId(d.template?.id ?? null);
      setNote(d.template?.overwritten ? `Updated “${n}”` : `Saved “${n}”`);
      setSaveOpen(false);
      load();
      return true;
    } catch {
      setNote('Could not save the template');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const pick = (t: Template) => {
    applyState(t.state);
    setSavedJson(JSON.stringify(t.state));
    setLoadedId(t.id);
    setName(t.mine ? t.name : '');
    setShared(t.mine ? t.shared : false);
    setListOpen(false);
    setNote(`Loaded “${t.name}” — press Search to run it`);
  };
  // After applyState the page re-renders with the template's filters: take
  // that as the saved state (the page may normalise what it was given).
  const settleRef = useRef(false);
  useEffect(() => {
    if (!settleRef.current) return;
    settleRef.current = false;
    setSavedJson(JSON.stringify(getState()));
  });
  const pickAndSettle = (t: Template) => { settleRef.current = true; pick(t); };
  useEffect(() => {
    if (baselineKey) setSavedJson(JSON.stringify(getState()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineKey]);

  const remove = async (t: Template) => {
    await fetch(`/api/search-templates/${t.id}`, { method: 'DELETE', headers: headers() }).catch(() => {});
    if (loadedId === t.id) { setLoadedId(null); setSavedJson(null); }
    load();
  };
  const toggleShare = async (t: Template) => {
    await fetch(`/api/search-templates/${t.id}`, { method: 'PATCH', headers: headers(), body: JSON.stringify({ shared: !t.shared }) }).catch(() => {});
    load();
  };

  const loaded = templates.find((t) => t.id === loadedId);
  const openSave = () => {
    setName(loaded && loaded.mine ? loaded.name : name);
    setSaveOpen(true); setListOpen(false); setNote(null);
  };

  return (
    <div className="mb-2 relative">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border bg-white text-xs font-medium ${accent.btn}`}
          onClick={() => { setListOpen(!listOpen); setSaveOpen(false); }}
          title="Searches you've saved to reuse"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
          Templates{templates.length ? ` (${templates.length})` : ''}
          <span className="opacity-60">▾</span>
        </button>
        <button
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border bg-white text-xs font-medium disabled:opacity-40 ${accent.btn}`}
          disabled={!hasFilters}
          onClick={openSave}
          title={hasFilters ? 'Save these filters as a template' : 'Add some filters first'}
        >
          + Save as template
        </button>
        {loaded && (
          <span className="text-xs text-gray-500 truncate max-w-full">
            Template: <span className="font-medium text-gray-700">{loaded.name}</span>
            {unsaved && <span className="text-amber-600"> · changed</span>}
          </span>
        )}
        {note && !loaded && <span className="text-xs text-gray-500">{note}</span>}
      </div>

      {listOpen && (
        <div className="absolute z-30 mt-1 w-80 max-w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          {templates.length === 0 ? (
            <div className="px-3 py-3 text-xs text-gray-500">
              No templates yet. Build a search, then press <strong>Save as template</strong> to reuse it any time.
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
              {templates.map((t) => (
                <div key={t.id} className={`flex items-center gap-2 px-3 py-2 hover:bg-gray-50 ${t.id === loadedId ? 'bg-gray-50' : ''}`}>
                  <button className="flex-1 min-w-0 text-left" onClick={() => pickAndSettle(t)}>
                    <div className="text-sm text-gray-800 truncate">{t.name}</div>
                    <div className="text-[10px] text-gray-400 truncate">
                      {t.mine ? (t.shared ? 'Shared with your team' : 'Only you') : `Shared by ${t.owner || 'a teammate'}`}
                      {' · '}{new Date(t.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </div>
                  </button>
                  {t.mine && user?.teamId && (
                    <button className="text-[10px] text-gray-400 hover:text-gray-700 whitespace-nowrap" onClick={() => toggleShare(t)}
                      title={t.shared ? 'Stop sharing with your team' : 'Share with your team'}>
                      {t.shared ? 'Unshare' : 'Share'}
                    </button>
                  )}
                  {t.mine && (
                    <button className="text-gray-300 hover:text-red-600" title="Delete template" onClick={() => remove(t)}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {saveOpen && (
        <div className="absolute z-30 mt-1 w-80 max-w-full bg-white border border-gray-200 rounded-xl shadow-lg p-3">
          <div className="text-xs font-semibold text-gray-700 mb-1.5">Save as template</div>
          <input
            autoFocus
            className={`w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 ${accent.ring}`}
            placeholder="e.g. Finance Directors – London"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setSaveOpen(false); }}
          />
          {user?.teamId && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600 mt-2">
              <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
              Share with my team
            </label>
          )}
          <div className="text-[10px] text-gray-400 mt-1.5">Saving under a name you already use updates that template.</div>
          {note && <div className="text-xs text-red-600 mt-1">{note}</div>}
          <div className="flex justify-end gap-2 mt-2">
            <button className="px-2.5 py-1 text-xs text-gray-500 hover:text-gray-800" onClick={() => setSaveOpen(false)}>Cancel</button>
            <button className={`px-3 py-1 rounded-lg text-xs font-medium text-white disabled:opacity-50 ${accent.solid}`} disabled={busy} onClick={save}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {leaveAsk && (
        <div className="fixed inset-0 z-[100] bg-black/30 flex items-center justify-center px-4" onClick={() => setLeaveAsk(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Leave without saving your search?</h3>
            <p className="text-sm text-gray-600 mt-1.5">
              The filters you've applied will be lost. Save them as a template to come back to them any time.
            </p>
            <input
              className={`mt-3 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 ${accent.ring}`}
              placeholder="Template name — e.g. Finance Directors – London"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {note && <div className="text-xs text-red-600 mt-1">{note}</div>}
            <div className="flex flex-wrap justify-end gap-2 mt-4">
              <button className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900" onClick={() => setLeaveAsk(null)}>Stay</button>
              <button className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => { const go = leaveAsk; setLeaveAsk(null); unsavedRef.current = false; go(); }}>
                Leave without saving
              </button>
              <button className={`px-3 py-1.5 text-sm rounded-lg font-medium text-white disabled:opacity-50 ${accent.solid}`} disabled={busy}
                onClick={async () => { const go = leaveAsk; if (await save()) { setLeaveAsk(null); unsavedRef.current = false; go(); } }}>
                {busy ? 'Saving…' : 'Save and leave'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
